import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export function clampPageIndex(page: number, totalPages: number): number {
  if (totalPages <= 0) return 0;
  return Math.min(Math.max(0, page), totalPages - 1);
}

export function paginateItems<T>(
  items: T[],
  pageZeroBased: number,
  pageSize: number,
): T[] {
  const start = pageZeroBased * pageSize;
  return items.slice(start, start + pageSize);
}

export function usePublicListingPager<T>({
  items,
  pageSize,
  initialPageZeroBased = 0,
  enabled = true,
}: {
  items: T[];
  pageSize: number;
  initialPageZeroBased?: number;
  enabled?: boolean;
}) {
  const [currentPage, setCurrentPage] = useState(() =>
    Math.max(0, initialPageZeroBased),
  );
  const listTopRef = useRef<HTMLDivElement>(null);
  const shouldScrollOnPageChangeRef = useRef(false);

  const totalPages = useMemo(
    () => Math.ceil(items.length / pageSize),
    [items.length, pageSize],
  );

  useEffect(() => {
    setCurrentPage((page) => clampPageIndex(page, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(
    () => paginateItems(items, currentPage, pageSize),
    [items, currentPage, pageSize],
  );

  const rangeStart =
    items.length === 0 ? 0 : currentPage * pageSize + 1;
  const rangeEnd = currentPage * pageSize + paginatedData.length;

  const scrollToListTop = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const element = listTopRef.current;
        if (!element) return;

        const elementPosition = element.getBoundingClientRect().top;
        window.scrollTo({
          top: elementPosition + window.scrollY - 130,
          behavior: "smooth",
        });
      });
    });
  }, []);

  const resetToFirstPage = useCallback(() => {
    setCurrentPage(0);
  }, []);

  const handlePrevious = useCallback(() => {
    shouldScrollOnPageChangeRef.current = true;
    setCurrentPage((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    shouldScrollOnPageChangeRef.current = true;
    setCurrentPage((prev) => Math.min(Math.max(totalPages - 1, 0), prev + 1));
  }, [totalPages]);

  useEffect(() => {
    if (!shouldScrollOnPageChangeRef.current) return;
    shouldScrollOnPageChangeRef.current = false;
    scrollToListTop();
  }, [currentPage, scrollToListTop]);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      if (event.key === "ArrowLeft" && currentPage > 0) {
        handlePrevious();
      } else if (event.key === "ArrowRight" && currentPage < totalPages - 1) {
        handleNext();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [currentPage, totalPages, handlePrevious, handleNext, enabled]);

  return {
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedData,
    listTopRef,
    rangeStart,
    rangeEnd,
    resetToFirstPage,
    handlePrevious,
    handleNext,
    canGoPrev: currentPage > 0,
    canGoNext: currentPage < totalPages - 1,
  };
}
